import { z } from "zod";
import { BusinessDate, Instant } from "./common.js";
import { Id } from "./ids.js";

/**
 * Integer per mille (parts per thousand). E.g. 150 = +15.0%, -400 = -40.0% (AG-14).
 */
export const PerMille = z.int();
export type PerMille = z.infer<typeof PerMille>;

/**
 * Iranian weekday index: 0 = شنبه, 1 = یکشنبه, 2 = دوشنبه, 3 = سه‌شنبه, 4 = چهارشنبه, 5 = پنج‌شنبه, 6 = جمعه.
 */
export const WeekdayIndex = z.int().min(0).max(6);
export type WeekdayIndex = z.infer<typeof WeekdayIndex>;

export const WeekdayName = z.enum(["شنبه", "یکشنبه", "دوشنبه", "سه‌شنبه", "چهارشنبه", "پنج‌شنبه", "جمعه"]);
export type WeekdayName = z.infer<typeof WeekdayName>;

export const HistoricalOrderLine = z.object({
  foodId: z.string().min(1),
  foodName: z.string().optional(),
  portions: z.int().nonnegative(),
});
export type HistoricalOrderLine = z.infer<typeof HistoricalOrderLine>;

export const HistoricalOrder = z.object({
  id: Id,
  unitId: Id,
  unitCode: z.string().optional(),
  unitName: z.string().optional(),
  organizationId: Id.optional(),
  organizationName: z.string().optional(),
  kitchenId: Id.optional(),
  businessDate: BusinessDate,
  mealId: z.string().min(1),
  status: z.enum(["completed", "delivered", "submitted", "cancelled"]).default("completed"),
  lines: z.array(HistoricalOrderLine).default([]),
});
export type HistoricalOrder = z.infer<typeof HistoricalOrder>;

export const PortionForecast = z.object({
  unitId: Id,
  unitName: z.string().optional(),
  mealId: z.string().min(1),
  weekday: WeekdayIndex,
  weekdayName: WeekdayName,
  medianPortions: z.int().nonnegative(),
  sampleCount: z.int().nonnegative(),
  sampleValues: z.array(z.int().nonnegative()),
  sampleDates: z.array(BusinessDate).optional(),
  skippedDates: z.array(BusinessDate).optional(),
  explanation: z.string(),
});
export type PortionForecast = z.infer<typeof PortionForecast>;

export const UnitTrend = z.object({
  unitId: Id,
  unitName: z.string().optional(),
  organizationId: Id.optional(),
  organizationName: z.string().optional(),
  mealId: z.string().min(1),
  recent4WeeksPortions: z.int().nonnegative(),
  prior4WeeksPortions: z.int().nonnegative(),
  trendPerMille: PerMille.nullable(),
  sampleCountRecent: z.int().nonnegative(),
  sampleCountPrior: z.int().nonnegative(),
  explanation: z.string(),
});
export type UnitTrend = z.infer<typeof UnitTrend>;

export const OrderDropAlertSeverity = z.enum(["important", "critical"]);
export type OrderDropAlertSeverity = z.infer<typeof OrderDropAlertSeverity>;

export const DetectedOrderDropAlert = z.object({
  unitId: Id,
  unitCode: z.string().optional(),
  unitName: z.string().optional(),
  dropPerMille: PerMille,
  recentAveragePortions: z.int().nonnegative(),
  usualPortions: z.int().nonnegative(),
  recentSamples: z.array(z.int().nonnegative()),
  reason: z.string(),
});
export type DetectedOrderDropAlert = z.infer<typeof DetectedOrderDropAlert>;

export const OrderDropAlert = z.object({
  id: Id,
  unitId: Id,
  unitCode: z.string().optional(),
  unitName: z.string().optional(),
  organizationId: Id.optional(),
  organizationName: z.string().optional(),
  severity: OrderDropAlertSeverity,
  dropPerMille: PerMille,
  recentAveragePortions: z.int().nonnegative(),
  usualPortions: z.int().nonnegative(),
  recentSamples: z.array(z.int().nonnegative()),
  reason: z.string(),
  detectedDate: BusinessDate,
  reviewedAt: Instant.nullable().optional(),
  reviewedBy: z.string().nullable().optional(),
  reviewNote: z.string().nullable().optional(),
  createdAt: Instant,
  updatedAt: Instant,
});
export type OrderDropAlert = z.infer<typeof OrderDropAlert>;

export const TomorrowFoodEstimate = z.object({
  foodId: z.string().min(1),
  foodName: z.string().optional(),
  expectedPortions: z.int().nonnegative(),
  orderedPortions: z.int().nonnegative().optional(),
  foodSharePerMille: PerMille,
});
export type TomorrowFoodEstimate = z.infer<typeof TomorrowFoodEstimate>;

export const TomorrowEstimate = z.object({
  targetDate: BusinessDate,
  weekday: WeekdayIndex,
  weekdayName: WeekdayName,
  mealId: z.string().min(1),
  kitchenId: Id.optional(),
  totalExpectedPortions: z.int().nonnegative(),
  totalOrderedPortions: z.int().nonnegative().optional(),
  lowPortions: z.int().nonnegative(),
  highPortions: z.int().nonnegative(),
  foods: z.array(TomorrowFoodEstimate),
  participatingUnitsCount: z.int().nonnegative(),
  explanation: z.string(),
});
export type TomorrowEstimate = z.infer<typeof TomorrowEstimate>;

export const ForecastHoliday = z.object({
  id: Id,
  businessDate: BusinessDate,
  title: z.string().min(1),
  createdAt: Instant,
  createdBy: z.string().nullable().optional(),
  archivedAt: Instant.nullable().optional(),
});
export type ForecastHoliday = z.infer<typeof ForecastHoliday>;

export const ForecastHolidayInput = z.object({
  businessDate: BusinessDate,
  title: z.string().min(1),
});
export type ForecastHolidayInput = z.infer<typeof ForecastHolidayInput>;

export const ForecastAccuracyItem = z.object({
  businessDate: BusinessDate,
  kitchenId: Id.optional(),
  expectedPortions: z.int().nonnegative(),
  actualPortions: z.int().nonnegative(),
  absoluteError: z.int().nonnegative(),
});
export type ForecastAccuracyItem = z.infer<typeof ForecastAccuracyItem>;

export const ForecastAccuracySummary = z.object({
  kitchenId: Id.optional(),
  period: z.string(),
  totalDays: z.int().nonnegative(),
  meanAbsoluteError: z.int().nonnegative(),
  explanation: z.string(),
});
export type ForecastAccuracySummary = z.infer<typeof ForecastAccuracySummary>;

export const ReviewAlertInput = z.object({
  note: z.string().max(500).optional(),
});
export type ReviewAlertInput = z.infer<typeof ReviewAlertInput>;

export const ForecastSettings = z.object({
  sampleWindowWeeks: z.int().min(1).max(52).default(4),
  dropThresholdPerMille: PerMille.default(300),
  criticalDropThresholdPerMille: PerMille.default(500),
  dropAlertHour: z.int().min(0).max(23).default(10),
});
export type ForecastSettings = z.infer<typeof ForecastSettings>;
